import jwtDecode from 'jwt-decode';

/*
 * Read-only admin view ("impersonation").
 *
 * The back office opens `<workplace>/impersonate#token=<jwt>`. The token is a
 * short-lived web access token with an `impersonation: true` claim and no
 * refresh token. Everything here is plain JS with injectable browser objects
 * so it can run before the app boots (and before Sentry starts recording the
 * URL) and be unit tested without a DOM.
 */

export const IMPERSONATION_PATH = '/impersonate';
export const IMPERSONATION_LANDING_PATH = '/dashboard';
export const IMPERSONATION_SESSION_FLAG = 'impersonation';

const ACCESS_TOKEN_KEY = 'jwt_access_token';
const REFRESH_TOKEN_KEY = 'jwt_refresh_token';

const safeStorage = (getStorage) => {
  try {
    return getStorage() || null;
  } catch {
    return null;
  }
};

/** Returns the `token` parameter of a URL fragment such as `#token=abc`, or null. */
export const parseImpersonationFragment = (hash) => {
  if (!hash || typeof hash !== 'string') {
    return null;
  }

  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const token = params.get('token');

  return token ? token.trim() || null : null;
};

/** Decodes a JWT without verifying it; returns null for anything that is not a JWT. */
export const decodeTokenClaims = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

export const isImpersonationToken = (token) => decodeTokenClaims(token)?.impersonation === true;

const UNKNOWN_SESSION_ID = 'unknown';

/** Identifies one impersonation session: the token's `jti` claim. */
export const impersonationSessionId = (token) => {
  const jti = decodeTokenClaims(token)?.jti;

  return jti !== undefined && jti !== null && jti !== '' ? String(jti) : UNKNOWN_SESSION_ID;
};

/** The token this tab should act with: its own in-memory one when set, else the stored one. */
export const resolveOwnAccessToken = (tabToken, storedToken) => tabToken || storedToken || null;

/** '/impersonate', '/impersonate/' and '//impersonate' all count as the impersonation path. */
export const isImpersonationPath = (pathname) =>
  typeof pathname === 'string' &&
  pathname.replace(/\/+/g, '/').replace(/(.)\/$/, '$1') === IMPERSONATION_PATH;

/**
 * Moves an impersonation token from the URL fragment into the regular session
 * storage and rewrites the URL so the token does not stay in the address bar,
 * the tab's session history or error-tracking breadcrumbs.
 *
 * A `token=` fragment is removed on every path; it is only stored as a
 * session on the impersonation path.
 *
 * Known limitation: `replaceState` cannot remove the original
 * `/impersonate#token=...` URL from the browser's global history database or
 * address-bar autocomplete. It stays there on the admin's machine; the token
 * in it is only usable for its 30-minute lifetime and cannot be refreshed.
 *
 * Returns true when a token was consumed.
 */
export const consumeImpersonationFragment = (win = typeof window !== 'undefined' ? window : undefined) => {
  if (!win?.location) {
    return false;
  }

  const onImpersonationPath = isImpersonationPath(win.location.pathname);
  const token = parseImpersonationFragment(win.location.hash);

  if (!onImpersonationPath) {
    if (token) {
      win.history?.replaceState(null, '', `${win.location.pathname}${win.location.search || ''}`);
    }

    return false;
  }

  const local = safeStorage(() => win.localStorage);
  const session = safeStorage(() => win.sessionStorage);

  if (token && local) {
    local.setItem(ACCESS_TOKEN_KEY, token);
    // A leftover refresh token of a real session would silently switch the
    // identity once the impersonation token expires.
    local.removeItem(REFRESH_TOKEN_KEY);
    // The flag names the consumed token (its `jti`), so a later reload can
    // tell this tab's session from one another read-only tab stored since.
    session?.setItem(IMPERSONATION_SESSION_FLAG, impersonationSessionId(token));
  }

  // Drop the fragment even when it is malformed.
  win.history?.replaceState(null, '', IMPERSONATION_LANDING_PATH);

  return Boolean(token && local);
};

/** The tab flag's value: the `jti` of the token this tab consumed, or null. */
export const getImpersonationFlag = (win = typeof window !== 'undefined' ? window : undefined) =>
  safeStorage(() => win?.sessionStorage)?.getItem(IMPERSONATION_SESSION_FLAG) || null;

export const hasImpersonationFlag = (win = typeof window !== 'undefined' ? window : undefined) =>
  Boolean(getImpersonationFlag(win));

export const clearImpersonationFlag = (win = typeof window !== 'undefined' ? window : undefined) => {
  safeStorage(() => win?.sessionStorage)?.removeItem(IMPERSONATION_SESSION_FLAG);
};

const getStoredAccessToken = (win = typeof window !== 'undefined' ? window : undefined) =>
  safeStorage(() => win?.localStorage)?.getItem(ACCESS_TOKEN_KEY) || null;

/** True when the current session token is an impersonation token (decided by its claims). */
export const isImpersonating = (win) => isImpersonationToken(getStoredAccessToken(win));

/** Company name carried by the token (`client` claim), used as a fallback for the banner. */
export const getImpersonatedCompanyFromToken = (win) =>
  decodeTokenClaims(getStoredAccessToken(win))?.client || null;

export const bearerToken = (authorization) =>
  typeof authorization === 'string' && authorization.startsWith('Bearer ')
    ? authorization.slice('Bearer '.length)
    : null;

/** True when an Authorization header value carries an impersonation token. */
export const isImpersonationAuthorization = (authorization) =>
  isImpersonationToken(bearerToken(authorization));

/**
 * Whether a request that failed with 4001 may be retried with the token that
 * is now in (shared, cross-tab) localStorage instead of refreshing.
 *
 * Only when the stored token belongs to the same identity as the sent one
 * (same `id`, `clientKey` and `impersonation` claims), and never for a
 * read-only admin session: its expiry must end the session, not switch it to
 * whatever token another tab stored.
 */
export const canRetryWithStoredToken = ({ sentAuthorization, storedToken, tabImpersonating }) => {
  if (tabImpersonating || !storedToken) {
    return false;
  }

  const sentToken = bearerToken(sentAuthorization);
  if (!sentToken || sentToken === storedToken) {
    return false;
  }

  const sent = decodeTokenClaims(sentToken);
  const stored = decodeTokenClaims(storedToken);
  if (!sent || !stored || sent.impersonation === true) {
    return false;
  }

  return (
    sent.id !== undefined &&
    sent.id === stored.id &&
    sent.clientKey === stored.clientKey &&
    Boolean(sent.impersonation) === Boolean(stored.impersonation)
  );
};

export const BOOT_SESSION = {
  NONE: 'none',
  VALID: 'valid',
  EXPIRED: 'expired',
  IMPERSONATION_EXPIRED: 'impersonationExpired',
  // The tab was a read-only admin view, but the stored token is not its
  // token any more (another tab stored its own session or another
  // read-only session).
  IMPERSONATION_REPLACED: 'impersonationReplaced',
};

/** Decides what to do with the stored token when the app boots. */
export const resolveBootSession = ({ accessToken, tabImpersonating, tabSessionId, isTokenValid }) => {
  if (!accessToken) {
    return BOOT_SESSION.NONE;
  }

  const impersonationToken = isImpersonationToken(accessToken);

  if (tabImpersonating && !impersonationToken) {
    return BOOT_SESSION.IMPERSONATION_REPLACED;
  }

  // Another read-only tab stored a different impersonation token since
  // (e.g. another client's workspace was opened): never boot as that one.
  if (tabImpersonating && tabSessionId && tabSessionId !== impersonationSessionId(accessToken)) {
    return BOOT_SESSION.IMPERSONATION_REPLACED;
  }

  if (isTokenValid(accessToken)) {
    return BOOT_SESSION.VALID;
  }

  return impersonationToken ? BOOT_SESSION.IMPERSONATION_EXPIRED : BOOT_SESSION.EXPIRED;
};
